/**
 * The four-beat loop, plus the shell screens around it.
 *
 * DRAW -> BRIEFING -> DRIVE_OUT -> COLLECTED -> DRIVE_BACK -> ARRIVED -> REWARD
 *
 * The Hub is where the game starts. There is no boot phase: it was a green disc with a play triangle on
 * it, and it existed less as a screen than as a way to collect the user gesture iOS needs before it will
 * make a sound. That gesture is now taken from her first press wherever it lands (`src/main.ts`), and the
 * Lookout's own entrance is the intro. See docs/MENUS.md.
 *
 * COLLECTED and ARRIVED are short celebration beats rather than interactive phases; they exist as
 * their own phases because the moment of collecting is the emotional peak of the loop and deserves
 * to be a thing the state machine knows about rather than an animation buried in the drive screen.
 */
export const Phase = {
  Hub: 'HUB',
  Garage: 'GARAGE',
  /** Everything she has earned, and everything still to earn. */
  Collection: 'COLLECTION',
  /**
   * The parent's screen: volumes, install state, a copy of everything, and the way to delete it all.
   *
   * A phase rather than a dialog over the Lookout, for the reason `docs/MENUS.md` gives: it must not look
   * like her game, and half a screen of near-black over a sky is a game with something wrong with it. It is
   * reached only by a three-second hold in the corner of the Lookout followed by a shown-once sequence, and
   * it is the one screen in the build the wordless rule does not apply to.
   */
  Parent: 'PARENT',
  Draw: 'DRAW',
  Briefing: 'BRIEFING',
  DriveOut: 'DRIVE_OUT',
  Collected: 'COLLECTED',
  DriveBack: 'DRIVE_BACK',
  Arrived: 'ARRIVED',
  Reward: 'REWARD',
  FreePlay: 'FREE_PLAY',
} as const;

export type Phase = (typeof Phase)[keyof typeof Phase];

export const ALL_PHASES: readonly Phase[] = Object.values(Phase);

/** The two phases where the car is under the player's fingers. */
export function isDrivingPhase(phase: Phase): boolean {
  return phase === Phase.DriveOut || phase === Phase.DriveBack || phase === Phase.FreePlay;
}

/** True while the player is on the outbound leg (used for camera look-ahead and the HUD ribbon). */
export function isOutbound(phase: Phase): boolean {
  return phase === Phase.DriveOut;
}
